import type { CidianSuggestion } from "../content/cidian/cidian-suggestion-line.js";
import {
  currentOption,
  expandCombobox,
  listId,
  openCombobox,
  optionId,
} from "./search-combobox.js";
import { suggestionOption } from "./suggestion-option.js";

/**
 * The list under the search box, and the cursor moving through it.
 *
 * **The theme draws the wrapper and this builds the list.** The list only ever
 * has anything in it once a script has run, so a reader with none is owed no
 * element for it and `partials/search-form.html` draws none. What the theme
 * does draw is the wrapper the list is positioned against. That costs such a
 * reader an empty `<div>`.
 *
 * **Native elements, where a `<datalist>` would have been the whole of this
 * with no script at all.** A browser filters a datalist's options by what the
 * box holds. A reader who typed `renshi` would therefore be shown an empty
 * list, 认识 holding none of those letters. The reading is the point of
 * suggesting, so the filtering belongs here.
 *
 * `<wa-dropdown>` is out for the reason the box itself is native. The pages
 * carrying this load Web Awesome's stylesheet and none of its JavaScript. A
 * custom element here would draw an empty space. See `modules/search.scss`.
 *
 * `suggestion-option.ts` draws one row and `search-combobox.ts` is what the
 * box says about all of them.
 */

/** What the list is drawn under and beside. */
export interface SuggestionListProperties {
  /** The wrapper the list is positioned against. */
  readonly around: HTMLElement;

  /** The box, which the roles are put on. */
  readonly input: HTMLInputElement;

  /**
   * Which box on the page this is, counting from nothing.
   *
   * The ids are built from it. A 词典 or 例句 page draws one box in the rail
   * and one in the article, and every id in a document has to be its own.
   */
  readonly box: number;
}

/** The suggestions as a reader sees them, and which one they are on. */
export class SuggestionList {
  readonly #input: HTMLInputElement;
  readonly #list: HTMLUListElement;
  readonly #box: number;
  #shown: readonly CidianSuggestion[] = [];
  #at = -1;

  constructor(properties: SuggestionListProperties) {
    this.#input = properties.input;
    this.#box = properties.box;
    this.#list = properties.around.ownerDocument.createElement("ul");
    this.#list.className = "cb-search-suggestions";
    this.#list.id = listId(this.#box);
    this.#list.setAttribute("role", "listbox");
    this.#list.hidden = true;
    properties.around.append(this.#list);
    openCombobox(this.#input, this.#box);
  }

  /**
   * Whether there is anything to arrow through.
   *
   * Compared with `false` rather than negated. `hidden` is a boolean and the
   * string `until-found`, and a union of the two has no honest truthiness.
   */
  get isOpen(): boolean {
    return this.#list.hidden === false;
  }

  /** The word under the cursor, or nothing where the reader is still typing. */
  get chosen(): string | undefined {
    return this.#shown[this.#at]?.word;
  }

  /** Draw these suggestions, or put the list away where there are none. */
  show(suggestions: readonly CidianSuggestion[]): void {
    if (suggestions.length === 0) {
      this.hide();

      return;
    }

    const page = this.#list.ownerDocument;

    this.#shown = suggestions;
    this.#at = -1;
    this.#list.replaceChildren(
      ...suggestions.map((suggestion, at) =>
        suggestionOption({ page, suggestion, id: optionId(this.#box, at) }),
      ),
    );
    this.#list.hidden = false;
    expandCombobox(this.#input, true);
  }

  /** Put the list away, and leave nothing behind claiming to be open. */
  hide(): void {
    this.#shown = [];
    this.#at = -1;
    this.#list.hidden = true;
    this.#list.replaceChildren();
    expandCombobox(this.#input, false);
  }

  /**
   * Move the cursor by a step, wrapping at both ends.
   *
   * Wrapping through the box itself, the position before the first option.
   * Arrowing up from the first suggestion therefore gives a reader back what
   * they typed instead of jumping them to the last one.
   */
  move(step: number): void {
    const places = this.#shown.length + 1;

    this.#at = ((this.#at + 1 + step + places) % places) - 1;

    currentOption({ input: this.#input, list: this.#list, at: this.#at });
  }
}
